<template>
  <Card
    :interactive="true"
    :glow="false"
    variant="elevated"
    class="personal-ad-card group relative h-full overflow-hidden"
    @click="$emit('click', ad)"
  >
    <template #header>
      <div class="flex items-start justify-between gap-3">
        <div class="flex-1 min-w-0">
          <Heading
            level="3"
            class="truncate font-headline text-title-lg text-primary transition-colors duration-300"
          >
            {{ ad.title }}
          </Heading>
          <div class="flex items-center gap-2 mt-1">
            <Text
              variant="caption"
              class="text-on-surface-variant transition-colors duration-300"
            >
              {{ formatTimeAgo(ad.created_at) }}
            </Text>
          </div>
        </div>
        <!-- Show replies counter ONLY when the ad belongs to the current user -->
        <div
          v-if="isMyAd && ad.reply_count > 0"
          class="flex items-center gap-1 text-secondary transition-all duration-300 group-hover:scale-110"
        >
          <MessageCircle class="w-4 h-4" />
          <Text variant="caption" class="font-medium">
            {{ ad.reply_count }}
          </Text>
        </div>
      </div>
    </template>

    <div class="flex flex-col h-full gap-4 flex-1">
      <!-- Body -->
      <Text
        variant="body"
        class="font-body text-body-md leading-relaxed text-on-surface-variant transition-colors duration-300"
      >
        {{ ad.body }}
      </Text>
    </div>
  </Card>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useUserStore } from '../stores/user'
import Card from './ui/Card.vue'
import Heading from './ui/Heading.vue'
import Text from './ui/Text.vue'
import { MessageCircle } from 'lucide-vue-next'

// Props
const props = defineProps({
  ad: {
    type: Object,
    required: true
  }
})

// Emits
const emit = defineEmits(['click', 'reply', 'view-replies', 'edit', 'delete'])

// Stores & i18n
const { t } = useI18n()
const userStore = useUserStore()
// Consider both strict equality and any precomputed is_mine flag, without leaking poster identity in UI
const isMyAd = computed(() => {
  if (!userStore?.profile?.id) return false
  if (props.ad?.poster_id) return userStore.profile.id === props.ad.poster_id
  return !!props.ad?.is_mine
})

// Helper functions
function formatTimeAgo(dateString) {
  const date = new Date(dateString)
  const now = new Date()
  const diffInMinutes = Math.floor((now - date) / (1000 * 60))

  if (diffInMinutes < 1) return 'just now'
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`

  const diffInHours = Math.floor(diffInMinutes / 60)
  if (diffInHours < 24) return `${diffInHours}h ago`

  const diffInDays = Math.floor(diffInHours / 24)
  if (diffInDays < 7) return `${diffInDays}d ago`

  const diffInWeeks = Math.floor(diffInDays / 7)
  if (diffInWeeks < 4) return `${diffInWeeks}w ago`

  return date.toLocaleDateString()
}

function formatPostDate(dateString) {
  const date = new Date(dateString)
  const now = new Date()
  const diffInDays = Math.floor((now - date) / (1000 * 60 * 60 * 24))

  if (diffInDays === 0) return 'today'
  if (diffInDays === 1) return 'yesterday'
  if (diffInDays < 7) return `${diffInDays} days ago`
  if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`

  return date.toLocaleDateString()
}
</script>

<style scoped>
.personal-ad-card {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  position: relative;
  overflow: hidden;
}

.personal-ad-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
}

.personal-ad-card:active {
  transform: translateY(0);
  transition: all 0.1s ease;
}

.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.icon {
  display: inline-block;
  width: 16px;
  height: 16px;
  text-align: center;
  font-size: 12px;
}
</style>
